import type { ReceiptWidthMm } from '@vuelto/print';
import { Platform } from 'react-native';
import * as Print from 'expo-print';

export type PrintHtmlDocumentOptions = {
  /** Texto plano del ticket; en web muestra acciones sin abrir Imprimir solo. */
  plainText?: string;
  /** @deprecated Web ya no descarga blob; se conserva por compat de callers. */
  downloadFilename?: string;
  widthMm?: ReceiptWidthMm;
};

const DEFAULT_TICKET_WIDTH_MM: ReceiptWidthMm = 72;
const TOOLBAR_MIN_WIDTH_PX = 340;

function mmToPx(mm: number): number {
  return Math.round(mm * 3.7795275591);
}

function receiptWindowTitle(plainText: string): string {
  for (const rawLine of plainText.split('\n')) {
    const pedido = rawLine.trim().match(/Pedido\s+#(\d+)/i);
    if (pedido?.[1]) return `Ticket · Pedido #${pedido[1]}`;
    const ref = rawLine.trim().match(/Ref\.?\s*(#\w+)/i);
    if (ref?.[1]) return `Ticket · ${ref[1]}`;
    if (/PRECUENTA/i.test(rawLine) || /Resumen del pedido/i.test(rawLine)) {
      return 'Cuenta · Vuelto';
    }
  }
  return 'Ticket · Vuelto';
}

function buttonStyle(primary: boolean): string {
  return [
    'padding:8px 14px',
    'border-radius:8px',
    'border:1px solid #111',
    primary ? 'background:#111;color:#fff' : 'background:#fff;color:#111',
    'font-weight:600',
    'cursor:pointer',
    'font-size:13px',
  ].join(';');
}

/**
 * Screen = toolbar + ticket. Print = solo ticket, mismo CSS tipográfico que el preview.
 * Un solo HTML → Imprimir y Guardar PDF (diálogo del browser) quedan iguales.
 */
function injectPreviewChromeStyles(doc: Document): void {
  const style = doc.createElement('style');
  style.textContent = `
    @page { size: 72mm auto; margin: 4mm; }
    @media screen {
      html {
        background: #e5e7eb;
        height: auto;
      }
      body {
        margin: 10px auto;
        padding: 0;
        background: transparent;
        box-shadow: none;
        border-radius: 0;
        max-width: 72mm;
        width: 72mm;
        box-sizing: border-box;
      }
      #vuelto-print-toolbar {
        margin: 0 0 14px;
        padding: 10px;
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        border-radius: 10px;
        font-family: system-ui, sans-serif;
        font-size: 13px;
        line-height: 1.45;
        color: #111;
        box-sizing: border-box;
        width: 100%;
      }
      #vuelto-ticket-sheet {
        margin: 0;
        padding: 8px 12px 10px;
        background: #fff;
        box-shadow: 0 1px 4px rgba(0,0,0,.12);
        border-radius: 4px;
        box-sizing: border-box;
        width: 100%;
      }
    }
    @media print {
      html, body {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 72mm !important;
        max-width: 72mm !important;
      }
      #vuelto-print-toolbar { display: none !important; }
      #vuelto-ticket-sheet {
        margin: 0 !important;
        padding: 8px 12px 10px !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
        width: 100% !important;
      }
    }
  `;
  doc.head.appendChild(style);
}

function injectWebPrintToolbar(doc: Document): void {
  const ticket = doc.createElement('div');
  ticket.id = 'vuelto-ticket-sheet';
  while (doc.body.firstChild) {
    ticket.appendChild(doc.body.firstChild);
  }

  const toolbar = doc.createElement('div');
  toolbar.id = 'vuelto-print-toolbar';

  const hint = doc.createElement('p');
  hint.textContent = 'En el diálogo, elegí Guardar como PDF.';
  hint.setAttribute('style', 'margin:0 0 8px;color:#374151;font-size:12px');

  const row = doc.createElement('div');
  row.setAttribute('style', 'display:flex;gap:8px;flex-wrap:wrap');

  const savePdfBtn = doc.createElement('button');
  savePdfBtn.type = 'button';
  savePdfBtn.textContent = 'Guardar PDF';
  savePdfBtn.setAttribute('style', buttonStyle(true));
  savePdfBtn.addEventListener('click', () => {
    winPrint(doc.defaultView);
  });

  const printBtn = doc.createElement('button');
  printBtn.type = 'button';
  printBtn.textContent = 'Imprimir';
  printBtn.setAttribute('style', buttonStyle(false));
  printBtn.addEventListener('click', () => {
    winPrint(doc.defaultView);
  });

  row.appendChild(savePdfBtn);
  row.appendChild(printBtn);
  toolbar.appendChild(hint);
  toolbar.appendChild(row);

  doc.body.appendChild(toolbar);
  doc.body.appendChild(ticket);
}

function centerPreviewWindow(win: Window): void {
  try {
    const left = Math.max(0, Math.round((window.screen.availWidth - win.outerWidth) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - win.outerHeight) / 2));
    win.moveTo(left, top);
  } catch {
    /* algunos browsers restringen moveTo */
  }
}

function fitPreviewWindow(win: Window, widthMm: ReceiptWidthMm): void {
  const doc = win.document;
  const ticketPx = mmToPx(widthMm);
  const contentWidth = Math.max(ticketPx + 48, TOOLBAR_MIN_WIDTH_PX);

  const applySize = () => {
    const chromeH = Math.max(win.outerHeight - win.innerHeight, 0);
    const chromeW = Math.max(win.outerWidth - win.innerWidth, 0);
    const contentHeight = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
    const winWidth = Math.min(contentWidth + chromeW, window.screen.availWidth - 16);
    const winHeight = Math.min(
      contentHeight + chromeH + 2,
      window.screen.availHeight - 16,
    );

    try {
      win.resizeTo(winWidth, Math.max(winHeight, 240));
      centerPreviewWindow(win);
    } catch {
      /* algunos browsers restringen resizeTo / moveTo */
    }
  };

  applySize();
  win.requestAnimationFrame(() => {
    applySize();
  });
}

function winPrint(view: Window | null): void {
  if (!view) return;
  try {
    view.focus();
    view.print();
  } catch {
    /* ignore */
  }
}

function openPrintPreviewWindow(html: string, widthMm: ReceiptWidthMm): Window {
  const ticketPx = mmToPx(widthMm);
  const initialWidth = Math.max(ticketPx + 48, TOOLBAR_MIN_WIDTH_PX);
  const initialHeight = 420;
  const initialLeft = Math.max(0, Math.round((window.screen.availWidth - initialWidth) / 2));
  const initialTop = Math.max(0, Math.round((window.screen.availHeight - initialHeight) / 2));
  const win = window.open(
    '',
    '_blank',
    `width=${initialWidth},height=${initialHeight},left=${initialLeft},top=${initialTop}`,
  );
  if (!win) {
    throw new Error(
      'El navegador bloqueó la ventana de impresión. Permití popups e intentá de nuevo.',
    );
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  return win;
}

/**
 * Print an HTML document.
 * Native: expo-print uses the provided HTML.
 * Web: preview del mismo HTML. Imprimir / Guardar PDF = window.print() (PDF vectorial del browser).
 */
export async function printHtmlDocument(
  html: string,
  options?: PrintHtmlDocumentOptions,
): Promise<void> {
  if (Platform.OS !== 'web') {
    await Print.printAsync({ html });
    return;
  }

  if (typeof window === 'undefined') {
    throw new Error('Impresión no disponible');
  }

  const plainText = options?.plainText?.trim();
  const widthMm = options?.widthMm ?? DEFAULT_TICKET_WIDTH_MM;

  const win = openPrintPreviewWindow(html, widthMm);

  await new Promise<void>((resolve, reject) => {
    const onReady = () => {
      try {
        if (plainText) {
          win.document.title = receiptWindowTitle(plainText);
          injectPreviewChromeStyles(win.document);
          injectWebPrintToolbar(win.document);
          fitPreviewWindow(win, widthMm);
          win.focus();
          resolve();
          return;
        }

        win.document.title = 'Imprimir · Vuelto';
        fitPreviewWindow(win, widthMm);
        win.focus();
        win.addEventListener('afterprint', () => {
          try {
            win.close();
          } catch {
            /* ignore */
          }
        });
        setTimeout(() => {
          try {
            win.close();
          } catch {
            /* ignore */
          }
        }, 120_000);
        winPrint(win);
        resolve();
      } catch (e) {
        try {
          win.close();
        } catch {
          /* ignore */
        }
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    if (win.document.readyState === 'complete') {
      onReady();
    } else {
      win.onload = () => onReady();
    }
  });
}
